import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Link,
    Switch,
    FormControlLabel,
    CircularProgress,
    Alert
} from '@mui/material';
import { grey } from '@mui/material/colors';
import GoogleSignUpButton from "../../components/GoogleRegisterButton";
import { sendOTP, verifyOTP } from "../../services/authService";

// Import images from assets
import backgroundImage from "../../assets/images/login-bkg.png";
import loginImage from "../../assets/images/log-chat.svg";
import blackLogo from "../../assets/images/blackLogo.svg";
import LinkedInSignUpButton from "../../components/LinkedInRegister";
import { GoogleOAuthProvider } from '@react-oauth/google';


function Register() {
    let userData = localStorage.getItem('userInfo')
    const [loading, setLoading] = useState(true);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState('warning');
    const [otpLoading, setOtpLoading] = useState(false);
    const [otpSent, setOtpSent] = useState(false);

     // not allowed domains for login
    const not_allowed_domains = ['gmail', 'hotmail', 'yahoo', 'outlook', 'aol'];
    const handleSubmit = async (e) => {
        e.preventDefault();

        const email = formData.email;
        const domain = email.split('@')[1].split('.')[0];
        if (not_allowed_domains.includes(domain)) {
            setShowAlert(true);
            setAlertSeverity('error');
            setAlertMessage('Please use a official email address');
            return;
        }
        
        if (!email) {
            setAlertSeverity('error');
            setAlertMessage('Please enter your email address');
            setShowAlert(true);
            return;
        }
    
        setOtpLoading(true);
        try {
            const response = await sendOTP(email, 'register');
            if (response.status === 'success') {
                setOtpSent(true);
                setAlertSeverity('success');
                setAlertMessage('OTP sent successfully! Please check your email.');
                setShowAlert(true);
            } else {
                setAlertSeverity('error');
                setAlertMessage(response.message || 'Failed to send OTP');
                setShowAlert(true);
            }
        } catch (error) {
            console.error('Error sending OTP:', error);
            setAlertSeverity('error');
            setAlertMessage(error.response?.data?.message || 'Failed to send OTP. Please try again.');
            setShowAlert(true);
        } finally {
            setOtpLoading(false);
        }
    };

    // handle otp submit
    const handleOtpSubmit = async (e) => {
        e.preventDefault();
        if (!formData.otp || formData.otp.length !== 6) {
            setAlertSeverity('error');
            setAlertMessage('Please enter a valid 6-digit OTP');
            setShowAlert(true);
            return;
        }
    
        if (!formData.termsAccepted) {
            setAlertSeverity('error');
            setAlertMessage('Please accept the Terms & Conditions and Privacy Policy');
            setShowAlert(true);
            return;
        }
    
        setOtpLoading(true);
        try {
            const response = await verifyOTP(formData.email, formData.otp, 'register');
            if (response.status === 'success') {
                localStorage.setItem('userInfo', JSON.stringify(response.data));
                setAlertSeverity('success');
                setAlertMessage('Registration successful! Redirecting to dashboard...');
                setShowAlert(true);
                // Redirect to dashboard after a short delay
                setTimeout(() => {
                    window.location.href = '/dashboard';
                }, 1500);
            } else {
                setAlertSeverity('error');
                setAlertMessage(response.message || 'OTP verification failed');
                setShowAlert(true);
            }
        } catch (error) {
            console.error('Error verifying OTP:', error);
            setAlertSeverity('error');
            setAlertMessage(error.response?.data?.message || 'Failed to verify OTP. Please try again.');
            setShowAlert(true);
        } finally {
            setOtpLoading(false);
        }
    };

  const [formData, setFormData] = React.useState({
    email: '',
    otp: '',
    newsUpdates: false,
    termsAccepted: false
  });

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: e.target.type === 'checkbox' ? checked : value
    }));
  };


  useEffect(() => {
    const checkUserInfo = () => {
      const userInfo = typeof window !== 'undefined' ? localStorage.getItem('userInfo') : null;      
      if (userInfo) {
        try {
          const parsed = JSON.parse(userInfo);
          window.location.href = '/dashboard';
        } catch (error) {
          console.log('Register page - error parsing userInfo:', error);
        }
      }
      setLoading(false);
    };
    
    checkUserInfo();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", width: "100vw", display: "flex", flexDirection: { xs: "column", md: "row" } }} >

      {/* left Side - Image (Hidden on xs and sm) */}
      <Box sx={{flex: 1, display: { xs: "none", md: "flex" }, background: `url(${backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', alignItems: 'center', justifyContent: 'center' }}>
        <img src={loginImage} alt="Login Visual" style={{ maxWidth: '80%', maxHeight: '80vh', objectFit: 'contain' }} />
      </Box>
      {/* left Side - Image (Hidden on xs and sm) */}
      
      {/* Right Side - Register Box */}
      <Box display="flex" flexDirection="column" sx={{px: 4, backgroundColor: grey[100], minHeight: "100vh", width: {xs: "100vw", md: "50vw"}}}>
        <Box sx={{pt: 4, pb: 2, color: "white", display: 'flex', justifyContent: { xs: 'center', md: 'center', sm: 'center' }, position:'inherit', top: 0, left: 0, zIndex: 1}}>
          <img src={blackLogo} alt="getLista" style={{ height: 40 }} />
        </Box> 
        <Box flexGrow={1} margin={{ xs: 0, sm: 1, md: 2 }} display="flex" alignItems="center" justifyContent="center" >
          <Paper elevation={3} sx={{p: { xs: 2, sm: 5 }, width: "100%", maxWidth: 600, borderRadius: 4, boxShadow: "0 4px 24px 0 rgba(56, 65, 74, 0.07)", background: "#ffffff", ml: {md: 8}, position: "relative"}}>
            
            {otpLoading && (
              <Box sx={{position: 'absolute', backgroundColor: 'rgba(255, 255, 255, 0.8)',display: 'flex',alignItems: 'center',justifyContent: 'center',zIndex: 1000,borderRadius: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {showAlert && (
              <Alert severity={alertSeverity} sx={{ mt: 2, mb: 2, width: '100%' }} onClose={() => {setShowAlert(false)}}>
                {alertMessage}
              </Alert>
            )}

            <Typography variant="h5" sx={{ fontWeight: 800, mb: 2, textAlign: "center", letterSpacing: "-0.03em", color: "black",}}>
              Sign Up to Getlista
            </Typography>
            <Typography variant="body2" sx={{ color: "black", mb: 3, textAlign: "center" }} >
              Welcome! Please enter your details.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, justifyContent: 'center' }}>
            <GoogleOAuthProvider  clientId="523077414487-nqglvq3pva83tsup3oknal9m6fcdistk.apps.googleusercontent.com">
            {/* <GoogleOAuthProvider clientId="241614144702-i5tlbf9svklkf6i2cevl9o1trisb1ojl.apps.googleusercontent.com"> */}


                <GoogleSignUpButton  />
                </GoogleOAuthProvider>
            
              
              <LinkedInSignUpButton />
            </Box>
            
            <Box sx={{ my: 2, textAlign: "center", color: "#000000", fontWeight: 800 }} >
              or
            </Box>
            
            <Box component="form" onSubmit={otpSent ? handleOtpSubmit : handleSubmit} autoComplete="off">
              <TextField 
                label="Official Email" 
                type="email" 
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                fullWidth 
                required 
                margin="normal" 
                variant="outlined"
                disabled={otpSent}
              />
              
              {otpSent && (
                <TextField 
                  label="Enter OTP" 
                  type="text" 
                  name="otp" 
                  value={formData.otp} 
                  onChange={handleInputChange} 
                  fullWidth 
                  required 
                  margin="normal" 
                  variant="outlined"
                  placeholder="Enter 6-digit OTP"
                  inputProps={{ maxLength: 6 }}
                />
              )}
              
              {!otpSent && (
                <>
                  <FormControlLabel
                    control={
                      <Switch
                        name="newsUpdates"
                        checked={formData.newsUpdates}
                        onChange={handleInputChange}
                        color="primary"
                      />
                    }
                    label="I agree to receive occasional news and updates."
                    sx={{ 
                      mt: 2, 
                      mb: 1,
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.875rem',
                        color: '#666'
                      }
                    }}
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        name="termsAccepted"
                        checked={formData.termsAccepted}
                        onChange={handleInputChange}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#666' }}>
                        I accept the
                        <Link href="/terms" target="_blank" sx={{ color: '#3b82f6', textDecoration: 'none' }}>
                          Terms & Conditions
                        </Link>
                        and
                        <Link href="/privacy" target="_blank" sx={{ color: '#3b82f6', textDecoration: 'none' }}>
                          Privacy Policy
                        </Link>
                      </Typography>
                    }
                    sx={{ mb: 2 }}
                  />
                </>
              )}
              
              <Button 
                type="submit" 
                variant="contained" 
                fullWidth 
                disabled={otpLoading || (!otpSent && !formData.termsAccepted)}
                sx={{ 
                  py: 1.5, 
                  fontWeight: 700, 
                  fontSize: "1rem", 
                  borderRadius: 2, 
                  background: "#1ce484", 
                  boxShadow: "none", 
                  mt: 2, 
                  mb: 2, 
                  textTransform: "none", 
                  "&:hover": { 
                    background: "#20ec84",
                  }
                }}
              >
                {otpLoading ? 'Sending OTP...' : (otpSent ? 'Verify OTP & Register' : 'Send OTP')}
              </Button>
            </Box>
            
            <Typography variant="body2" sx={{ textAlign: "center", color: "black" }}>
              Already have an account?{" "}
              <Link href="/login" underline="hover" sx={{ color: "#3b82f6", fontWeight: 600 }}>
                Sign in
              </Link>
            </Typography>
          </Paper>
        </Box>

    </Box>
    </Box>
    )
}

export default Register;
